// -----------------------------------------------------------------------------------------------------------------
// Restricted - Copyright (C) Siemens Healthineers AG 2023.
// -----------------------------------------------------------------------------------------------------------------
import { __decorate } from 'tslib';
import { html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { sharedStyles } from '../../styles';
import {
  dispatchAttributeChangedEventTypesafe,
  event,
  ShuiLitElement,
} from '../../utils/event-decorator.js';
import { CloseEvent } from '../../utils/events.js';
import notificationItemStyles from './sh-notification-item.lit.scss.js';
export const NotificationListOfProperties = [
  'label',
  'icon',
  'type',
  'message',
  'visible',
  'sticky',
  'flat',
  'new',
];
/**
 * @slot - Takes plain text or other components as children.
 * @slot footer - Takes button as children.
 * @fires close {CloseEvent}- Dispatched when clicking on the close icon and also on autoclose
 * @fires label-changed {LabelPropertyChangedEvent} - *hide emitted when the label property changes.
 * @fires icon-changed {IconPropertyChangedEvent} - *hide emitted when the icon property changes.
 * @fires type-changed {TypePropertyChangedEvent} - *hide emitted when the type property changes.
 * @fires message-changed {MessagePropertyChangedEvent} - *hide emitted when the message property changes.
 * @fires visible-changed {VisiblePropertyChangedEvent} - *hide emitted when the visible property changes.
 * @fires sticky-changed {StickyPropertyChangedEvent} - *hide emitted when the sticky property changes.
 * @fires flat-changed {FlatPropertyChangedEvent} - *hide emitted when the flat property changes.
 * @fires new-changed {NewPropertyChangedEvent} - *hide emitted when the new property changes.
 */
let SHNotificationItem = class SHNotificationItem extends ShuiLitElement {
  constructor() {
    super(...arguments);
    this.emptyFooter = true;
    /** Defines if the notification item has an empty message or not. */
    this.emptyMessage = true;
  }
  static get styles() {
    return [notificationItemStyles, sharedStyles];
  }
  render() {
    return html`
      ${this.label ? this.createHeaderWrapper() : ''} ${this.getDescription()}
      <div class="footer-wrapper" ?empty-footer=${this.emptyFooter}>
        <slot
          id="footer"
          name="footer"
          @slotchange="${(e) => {
            return this.updateFooterSlot(e.target);
          }}"
        ></slot>
      </div>
    `;
  }
  createHeaderWrapper() {
    if (this.visible === true && (this.type === 'confirmation' || this.type === 'alert')) {
      console.warn(
        "Notification type 'confirmation' and 'alert' are deprecated and will be removed in a future major release. Please transition to using 'success' and 'warning' as soon as possible to ensure compatibility with future updates."
      );
    }
    return html`
      <div class="header-wrapper">
        ${this.icon &&
        this.type !== 'alert' &&
        this.type !== 'confirmation' &&
        this.type !== 'error' &&
        this.type !== 'success' &&
        this.type !== 'warning'
          ? html` <sh-icon
              icon="${this.icon}"
              .color="${this.iconColor}"
              id="notification-icon"
              size="s"
            ></sh-icon>`
          : ''}
        ${this.type === 'error'
          ? html`<sh-icon
              icon="error"
              aria-label="${this.accessibleTypeLabel || this.type}"
              class="type-icon-error"
              size="s"
            ></sh-icon>`
          : ''}
        ${this.type === 'alert' || this.type === 'warning'
          ? html`<sh-icon
              icon="warning"
              aria-label="${this.accessibleTypeLabel || this.type}"
              class="type-icon-warning"
              size="s"
            ></sh-icon>`
          : ''}
        ${this.type === 'confirmation' || this.type === 'success'
          ? html`<sh-icon
              icon="success"
              aria-label="${this.accessibleTypeLabel || this.type}"
              class="type-icon-success"
              size="s"
            ></sh-icon>`
          : ''}
        ${this.label ? html`<sh-text size="header-2" id="label">${this.label}</sh-text>` : ''}
        ${this.new ? html`<sh-badge class="badge"></sh-badge>` : ''}
        ${!this.sticky && this.label
          ? html`<sh-icon
              button
              icon="cancel"
              aria-label="${this.accessibleCloseLabel || 'Close'}"
              size="s"
              tabindex="0"
              @click="${() => this.fireClosevent()}"
              id="close-button"
            ></sh-icon>`
          : ''}
      </div>
    `;
  }
  getDescription() {
    const classes = {
      default: true,
      'empty-body': this.emptyMessage && !this.message,
    };
    return html` <div class="${classMap(classes)}">
      ${!this.label ? this.getBodyIcon() : ''}
      <sh-text size="body-1" role="alert">
        ${this.message
          ? this.message
          : html`
              <slot
                @slotchange="${(e) => {
                  return this.updateMessageSlot(e.target);
                }}"
              ></slot>
            `}
      </sh-text>
      ${!this.sticky && !this.label
        ? html`
            <sh-icon
              button
              icon="cancel"
              aria-label="${this.accessibleCloseLabel || 'Close'}"
              size="s"
              tabindex="0"
              @click="${() => this.fireClosevent()}"
              id="close-button-2"
            ></sh-icon>
            ${this.new ? html`<sh-badge class="badge"></sh-badge>` : ''}
          `
        : ''}
    </div>`;
  }
  getBodyIcon() {
    return html`
      ${this.icon &&
      this.type !== 'alert' &&
      this.type !== 'confirmation' &&
      this.type !== 'error' &&
      this.type !== 'success' &&
      this.type !== 'warning'
        ? html` <sh-icon icon="${this.icon}" id="notification-icon" size="s"></sh-icon>`
        : ''}
      ${this.type === 'error'
        ? html`<sh-icon
            icon="error"
            aria-label="${this.accessibleTypeLabel || this.type}"
            class="type-icon-error"
            size="s"
          ></sh-icon>`
        : ''}
      ${this.type === 'alert' || this.type === 'warning'
        ? html`<sh-icon
            icon="warning"
            aria-label="${this.accessibleTypeLabel || this.type}"
            class="type-icon-warning"
            size="s"
          ></sh-icon>`
        : ''}
      ${this.type === 'confirmation' || this.type === 'success'
        ? html`<sh-icon
            icon="success"
            aria-label="${this.accessibleTypeLabel || this.type}"
            class="type-icon-success"
            size="s"
          ></sh-icon>`
        : ''}
    `;
  }
  updateFooterSlot(slot) {
    const numberOfNodes = slot.assignedNodes({
      flatten: true,
    }).length;
    this.emptyFooter = numberOfNodes === 0;
  }
  updateMessageSlot(slot) {
    const numberOfNodes = slot.assignedNodes({
      flatten: true,
    }).length;
    this.emptyMessage = numberOfNodes === 0;
  }
  fireClosevent() {
    this.visible = false;
    // fire event on closing
    this.closeEvent.emit(new CloseEvent());
  }
  updated(changedProperties) {
    if (changedProperties.has('visible')) {
      this.autoClose();
    }
    dispatchAttributeChangedEventTypesafe(this, changedProperties, NotificationListOfProperties);
  }
  autoClose() {
    const shortDuration = 2000;
    const longDuration = 5000;
    if (this.duration === 'short' && this.visible) {
      this.addsDelay(shortDuration);
    } else if (this.duration === 'long' && this.visible) {
      this.addsDelay(longDuration);
    } else if (this.duration && this.visible) {
      const customDuration = parseFloat(this.duration);
      this.addsDelay(customDuration);
    }
  }
  addsDelay(duration) {
    this.delay = setTimeout(() => {
      this.fireClosevent();
      clearTimeout(this.delay);
    }, duration);
  }
};
__decorate([event()], SHNotificationItem.prototype, 'labelChangedEvent', void 0);
__decorate([event()], SHNotificationItem.prototype, 'iconChangedEvent', void 0);
__decorate([event()], SHNotificationItem.prototype, 'typeChangedEvent', void 0);
__decorate([event()], SHNotificationItem.prototype, 'messageChangedEvent', void 0);
__decorate([event()], SHNotificationItem.prototype, 'visibleChangedEvent', void 0);
__decorate([event()], SHNotificationItem.prototype, 'stickyChangedEvent', void 0);
__decorate([event()], SHNotificationItem.prototype, 'flatChangedEvent', void 0);
__decorate([event()], SHNotificationItem.prototype, 'newChangedEvent', void 0);
__decorate([event()], SHNotificationItem.prototype, 'closeEvent', void 0);
__decorate(
  [property({ type: String, reflect: true })],
  SHNotificationItem.prototype,
  'label',
  void 0
);
__decorate(
  [property({ type: String, reflect: true })],
  SHNotificationItem.prototype,
  'icon',
  void 0
);
__decorate(
  [property({ type: String, reflect: true })],
  SHNotificationItem.prototype,
  'type',
  void 0
);
__decorate(
  [property({ type: String, reflect: true })],
  SHNotificationItem.prototype,
  'message',
  void 0
);
__decorate(
  [property({ type: String, reflect: true })],
  SHNotificationItem.prototype,
  'duration',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHNotificationItem.prototype,
  'visible',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHNotificationItem.prototype,
  'sticky',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHNotificationItem.prototype,
  'flat',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHNotificationItem.prototype,
  'new',
  void 0
);
__decorate(
  [property({ type: Boolean, reflect: true })],
  SHNotificationItem.prototype,
  'inline',
  void 0
);
__decorate(
  [property({ type: String, reflect: true, attribute: 'icon-color' })],
  SHNotificationItem.prototype,
  'iconColor',
  void 0
);
__decorate(
  [property({ type: String, attribute: 'accessible-type-label' })],
  SHNotificationItem.prototype,
  'accessibleTypeLabel',
  void 0
);
__decorate(
  [property({ type: String, attribute: 'accessible-close-label' })],
  SHNotificationItem.prototype,
  'accessibleCloseLabel',
  void 0
);
__decorate([state()], SHNotificationItem.prototype, 'emptyFooter', void 0);
__decorate([state()], SHNotificationItem.prototype, 'emptyMessage', void 0);
SHNotificationItem = __decorate([customElement('sh-notification-item')], SHNotificationItem);
export { SHNotificationItem };
